/**
 * Service worker registration.
 *
 * Skipped in development on purpose: a cache-first worker would serve yesterday's
 * bundle over hot reload, and the resulting "my change did nothing" confusion costs
 * more than the offline behaviour is worth on a machine that has signal.
 *
 * Registration failure is never fatal. A device policy, a private window, or an
 * unsupported browser all mean the app runs online-only -- degraded, but working.
 * Throwing here would turn a missing optimisation into a blank screen.
 */

export type DrainRequestHandler = () => void;

export function registerServiceWorker(onDrainRequested: DrainRequestHandler): void {
  if (import.meta.env.DEV || !('serviceWorker' in navigator)) {
    return;
  }

  // Registering after load keeps the worker's install off the critical path of the
  // first paint, which is the one an operator is standing there waiting for.
  //
  // But this is reached from an async bootstrap that first opens IndexedDB, so by the
  // time we get here the load event has usually already fired -- and it never fires
  // again. Waiting for it unconditionally means the worker silently never registers
  // and the app has no offline support at all, while appearing to work perfectly on a
  // desk with signal.
  whenLoaded(() => {
    void navigator.serviceWorker.register('/sw.js', { scope: '/' }).catch(() => {
      // Online-only from here. Nothing else to do.
    });
  });

  navigator.serviceWorker.addEventListener('message', (event: MessageEvent) => {
    if ((event.data as { type?: string } | undefined)?.type === 'drain-outbox') {
      onDrainRequested();
    }
  });
}

function whenLoaded(run: () => void): void {
  if (document.readyState === 'complete') {
    run();
    return;
  }

  window.addEventListener('load', run, { once: true });
}

/**
 * Asks the browser to keep this origin's storage rather than evicting it under
 * pressure. Granted on Chrome and Android; a no-op on iOS, where installing to the
 * home screen is the only thing that provides the same protection.
 */
export async function requestPersistentStorage(): Promise<boolean> {
  try {
    return (await navigator.storage?.persist?.()) ?? false;
  } catch {
    return false;
  }
}
